/**
 * The typing indicator of one open conversation: who else is typing (the
 * rider, or another agent about to answer the same rider) and a
 * `notifyTyping` the composer calls on every keystroke, throttled to one
 * chatTyping per 3 s. Rules in ./typing.ts.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { sendChatTyping } from './api';
import { subscribeTyping } from './socket';
import {
  EMPTY_TYPING,
  TypingLabels,
  TypingState,
  addTypingEvent,
  clearTyper,
  createThrottle,
  expireTypers,
  nextExpiry,
  typingLabels,
} from './typing';

export interface ChatTyping extends TypingLabels {
  /** Call as the agent types a reply: tells ant at most once per 3 s. */
  notifyTyping: () => void;
}

/**
 * `conversationId` is ant's CONVERSATION id (Conversation.conversation_id),
 * null for none (nothing is subscribed or sent). `lastMessage` is the
 * thread's newest message: a typer whose message lands stops typing.
 */
export function useChatTyping(
  conversationId: string | null | undefined,
  myAdminId: string | null,
  lastMessage?: { id: string; sender_id: string } | null
): ChatTyping {
  const [typers, setTypers] = useState<TypingState>(EMPTY_TYPING);
  const myAdminIdRef = useRef(myAdminId);
  myAdminIdRef.current = myAdminId;

  useEffect(() => {
    setTypers(EMPTY_TYPING);
    if (!conversationId) return undefined;
    return subscribeTyping(conversationId, (event) =>
      setTypers((prev) =>
        addTypingEvent(prev, event, {
          conversationId,
          myAdminId: myAdminIdRef.current,
          now: Date.now(),
        })
      )
    );
  }, [conversationId]);

  // Each typer goes 6 s after their last event.
  useEffect(() => {
    const next = nextExpiry(typers);
    if (next === null) return undefined;
    const timer = window.setTimeout(
      () => setTypers((prev) => expireTypers(prev, Date.now())),
      Math.max(0, next - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [typers]);

  // Their message landed: they are done typing it.
  const lastId = lastMessage?.id;
  const lastSender = lastMessage?.sender_id;
  useEffect(() => {
    if (lastSender) setTypers((prev) => clearTyper(prev, lastSender));
  }, [lastId, lastSender]);

  const throttle = useMemo(
    () =>
      createThrottle(() => {
        if (!conversationId) return;
        // Ephemeral: a lost typing event is not worth an error.
        sendChatTyping(conversationId).catch(() => undefined);
      }),
    [conversationId]
  );
  const notifyTyping = useCallback(() => {
    throttle.notify();
  }, [throttle]);

  const labels = useMemo(() => typingLabels(typers), [typers]);
  return { ...labels, notifyTyping };
}
